// TODO Bug 14: Timezone handling - Currently uses browser timezone implicitly.
// Consider adding explicit timezone support for multi-timezone teams.
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CalMeetingForm } from '../exo/cal/types/meeting/CalMeetingForm';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import findDifferences from '../exo/findDifferencesInDataCollection';
import { Box, Button, Typography } from '@mui/material';
import CalendarSelect from '../exo/cal/types/CalendarSelect';
import useFetch from '../../api/useFetch';
import uuid from 'react-uuid';

export const NewMeeting = ({
  startData,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
  onFeedback,
  debug,
  openParticipantsBlockTimes,
  setOpenParticipantsBlockTimes,
}) => {
  const { t } = useTranslation();
  const cookies = new Cookies();
  const userId = cookies.get('userId');
  const config = useConfig();

  const { apiData } = useFetch(`calendar/own?noEvents=true`);

  // Bug 9: Removed empty useEffect

  const [isValid, setIsValid] = useState(false);

  const [dataCollection, setDataCollection] = useState({});
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  // Bug 4: Use ref instead of state to avoid stale closure issue
  const autoReminderDeletedRef = useRef(false);

  function calculateAutomaticReminder(startMoment) {
    const now = moment();
    const diffMinutes = startMoment.diff(now, 'minutes');
    const diffHours = startMoment.diff(now, 'hours');
    const diffDays = startMoment.diff(now, 'days');

    if (diffMinutes <= 15) return null; // past or too close
    if (diffDays >= 1) {
      return {
        remindAt: startMoment.clone().subtract(1, 'days').toISOString(),
        uuid: uuid(),
        type: 'daysBefore',
        value: 1,
        isAutomatic: true,
      };
    }
    // Bug 6: Changed from 1 hour to 3 hours for better notice on medium-term meetings
    if (diffHours >= 12) {
      return {
        remindAt: startMoment.clone().subtract(3, 'hours').toISOString(),
        uuid: uuid(),
        type: 'hoursBefore',
        value: 3,
        isAutomatic: true,
      };
    }
    if (diffHours >= 1) {
      return {
        remindAt: startMoment.clone().subtract(15, 'minutes').toISOString(),
        uuid: uuid(),
        type: 'minutesBefore',
        value: 15,
        isAutomatic: true,
      };
    }
    return {
      remindAt: startMoment.clone().subtract(5, 'minutes').toISOString(),
      uuid: uuid(),
      type: 'minutesBefore',
      value: 5,
      isAutomatic: true,
    };
  }

  // handle form data changes
  function handleChange(newData) {
    setDataCollection((prev) => {
      const merged = { ...prev, ...newData };

      // Detect if automatic reminder was removed manually
      if (
        prev.reminders &&
        prev.reminders.some((r) => r.isAutomatic) &&
        (!merged.reminders || !merged.reminders.some((r) => r.isAutomatic))
      ) {
        autoReminderDeletedRef.current = true;
      }

      // Only recalc auto reminder if startDate or startTime changed
      if (newData.startDate || newData.startTime) {
        // Bug 8: Validate parsed moments before using them
        const parsedStartDate = moment(merged.startDate);
        const parsedStartTime = moment(merged.startTime);

        if (!parsedStartDate.isValid() || !parsedStartTime.isValid()) {
          return merged; // Early return on invalid dates
        }

        const startDate = parsedStartDate.format('YYYY-MM-DD');
        const startTime = parsedStartTime.format('HH:mm');
        const startMoment = moment(
          `${startDate} ${startTime}`,
          'YYYY-MM-DD HH:mm'
        );
        const now = moment();

        if (!autoReminderDeletedRef.current) {
          const diffMinutes = startMoment.diff(now, 'minutes');

          // Remove automatic reminder if in the past or too soon
          if (diffMinutes <= 15) {
            const newReminders = (merged.reminders || []).filter(
              (r) => !r.isAutomatic
            );
            console.log('REMOVING AUTO REMINDER');

            return { ...merged, reminders: newReminders };
          }

          // Calculate new automatic reminder
          const newAutoReminder = calculateAutomaticReminder(startMoment);
          if (newAutoReminder) {
            const others = (merged.reminders || []).filter(
              (r) => !r.isAutomatic
            );
            const existingAuto = (merged.reminders || []).find(
              (r) => r.isAutomatic
            );

            // Skip if the same automatic reminder already exists
            if (
              existingAuto &&
              Math.abs(
                moment(existingAuto.remindAt).diff(
                  newAutoReminder.remindAt,
                  'minutes'
                )
              ) <= 1
            ) {
              return merged;
            }

            return { ...merged, reminders: [...others, newAutoReminder] };
          }
        }
      }

      return merged;
    });
  }

  // startData functions
  function meetingDefaultData() {
    // Get the current time and round it up to the next 30-minute mark
    const now = moment();
    const roundedStart = now
      .clone()
      .add(30 - (now.minute() % 30), 'minutes')
      .startOf('minute');

    // Set the endDateTime to 30 minutes after the rounded start time
    const roundedEnd = roundedStart.clone().add(30, 'minutes');

    // Bug 5: Use consistent timestamps from same moment for date and time
    return {
      startTime: roundedStart.toISOString(),
      startDate: roundedStart.toISOString(),
      endTime: roundedEnd.toISOString(),
      endDate: roundedEnd.toISOString(),
      // Bug 11: API expects string '0'/'1' for these boolean-like fields
      wholeDay: '0',
      meetingIsConfirmed: '0',
      label: t('New Meeting'),
      type: 'meeting',
      blockTime: '0',
      owner: {
        id: userId,
      },
    };
  }

  // Bug 13: Memoize defaultData to prevent recalculation on every render
  const defaultData = useMemo(() => meetingDefaultData(), [userId, t]);
  useEffect(() => {
    if (startData) return setDataCollection({ ...defaultData, ...startData });

    setDataCollection(defaultData);
  }, [startData]);

  // submit
  // Bug 7: Default sendEmails to true
  function handleSubmit(sendEmails = true) {
    // Bug 1: Validate calendar selection before submitting
    if (!selectedCalendar?.id) {
      onFeedback && onFeedback(t('Please select a calendar'), 'error');
      return;
    }

    const startMoment = moment(dataCollection.startTime);
    const endMoment = moment(dataCollection.endTime);

    const startTime = startMoment.format('HH:mm');
    const startDate = moment(dataCollection.startDate).format('YYYY-MM-DD');
    const startMomentMerged = moment(
      `${startDate} ${startTime}`,
      'YYYY-MM-DD HH:mm'
    );

    const endTime = endMoment.format('HH:mm');
    const endDate = moment(dataCollection.endDate).format('YYYY-MM-DD');
    const endMomentMerged = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm');

    const reqData = {
      ...dataCollection,
      startDateTime: startMomentMerged.toISOString(),
      endDateTime: endMomentMerged.toISOString(),
      sendEmails: Boolean(sendEmails),
      calendarId: selectedCalendar.id,
    };

    DBRequest({
      config,
      path: dataCollection.id
        ? `calendar-events/${dataCollection.id}`
        : 'calendar-events',
      method: dataCollection.id
        ? API_PATCH(findDifferences(startData, reqData))
        : API_POST(reqData),
      onFeedback,
      onResponse: (res) => {
        dataCollection.id
          ? onUpdate({
              ...res.data,
              calendar: selectedCalendar,
              color: selectedCalendar.color,
            })
          : onCreate({
              ...res.data,
              calendar: selectedCalendar,
              color: selectedCalendar.color,
            });
        setDataCollection({});
        // Bug 3: Move onClose inside onResponse to wait for API success
        onClose();
      },
      // Bug 15: Add error handler
      onError: (error) => {
        onFeedback &&
          onFeedback(error?.message || t('Failed to save meeting'), 'error');
      },
    });
  }

  // calendar change & meeting move logic
  function handleCalendarChange(newCal) {
    if (dataCollection.id) handleMoveMeeting(dataCollection, newCal);
    setSelectedCalendar(newCal);
  }

  function handleMoveMeeting(meeting, newCal) {
    DBRequest({
      config,
      path: `calendar-events/${meeting.id}/move`,
      method: API_PATCH({
        fromCalendarId: meeting.calendar?.id,
        toCalendarId: newCal.id,
      }),
      onFeedback,
      // Bug 10: Add user feedback on error
      onError: (error) => {
        setSelectedCalendar(meeting.calendar);
        onFeedback &&
          onFeedback(
            error?.message || t('Failed to move meeting to new calendar'),
            'error'
          );
      },
    });
  }

  return (
    <Box className="flex flex-col h-full w-full justify-between gap-2  ">
      <Box className="h-full w-full flex flex-col overflow-y-auto px-3">
        {debug && (
          <Button
            onClick={() => console.log(dataCollection)}
            variant="contained"
            color="warning"
            sx={{ position: 'absolute' }}
            className="top-1 left-1"
          >
            debug
          </Button>
        )}
        <Typography variant="h3" textAlign="center">
          {t('New Meeting')}
        </Typography>
        <Box className="py-2">
          <CalendarSelect
            calendars={apiData?.data}
            selectedCal={selectedCalendar}
            onSelect={handleCalendarChange}
            size="large"
          />
        </Box>

        <CalMeetingForm
          dataCollection={dataCollection}
          onValidationChange={setIsValid}
          onChange={handleChange}
          openBlock={openParticipantsBlockTimes}
          setOpenBlock={setOpenParticipantsBlockTimes}
        />
      </Box>
      <Box className="w-full flex justify-between pb-2 px-3">
        <Button variant="outlined" color="error" onClick={onClose}>
          {t('Cancel')}
        </Button>
        {/* Bug 2: Disable Save when form is invalid or no calendar selected */}
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={!isValid || !selectedCalendar}
        >
          {t('Save')}
        </Button>
      </Box>
    </Box>
  );
};
